import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { isMobile } from "react-device-detect";
import { isEmpty, isNil } from "lodash";

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Typography,
  Collapse,
  LinearProgress,
} from "@material-ui/core";

import { FleetContext } from "../../context";
import { FilledSelect, FilledTextField } from "../../components/FormElements";
import QrCodeScanner from "./QrCodeScanner";

const SwapComponent = () => {
  const partTypes = ["", "sc", "md", "bm", "sim"];
  const provisioningStatuses = ["", "new", "used"];
  const { t } = useTranslation();
  const {
    swapSerializedComponent, swapSim, activeVehicle, activeSIMConfig,
  } = useContext(FleetContext);
  const [isOpen, setIsOpen] = useState(true);
  const [partType, setPartType] = useState(partTypes[0]);
  const [sn1, setSn1] = useState("");
  const [sn2, setSn2] = useState("");
  const [isMatching, setIsMatching] = useState(true);
  const [provisioned, setProvisioned] = useState(provisioningStatuses[0]);
  const [sim, setSim] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [failure, setFailure] = useState(null);
  const [showQr, setShowQr] = useState(false);
  const [partSN, setPartSN] = useState(null);
  const history = useHistory();
  const cluster = window.REACT_APP_CLUSTER_NAME;
  const vehicleComponents = activeVehicle?.components;

  const handleClose = (e = null) => {
    if (e) e.stopPropagation();
    setIsOpen(false);
    history.goBack();
  };

  const handleSubmit = async () => {
    setFailure(null);
    setIsLoading(true);

    try {
      if (partType === "sim") {
        await swapSim(activeVehicle.serial_number, sim ?? null, cluster);
      } else {
        await swapSerializedComponent(
          activeVehicle.serial_number,
          partType,
          sn1,
          provisioned === "used",
          sim ?? null,
          cluster,
        );
      }
      handleClose();
    } catch (err) {
      console.error(err);
      setFailure(parseError(err));
    }

    setIsValid(false);
    setIsLoading(false);
  };

  const parseError = (err) => {
    if (err.response && err.response.data) {
      if (err.response.data.error) {
        return err.response.data.error;
      }
      if (err.response.data instanceof Array) {
        let message = "";
        err.response.data.forEach((x) => {
          if (x instanceof Object) {
            // remove big whitespaces
            message += `${x.error.replace(/\s+/g, " ")} `;
          } else {
            message += `${x} `;
          }
        });
        return message;
      }
    }
    return err.message;
  };

  const handleQrScan = (text) => {
    setSn1(text);
    setSn2(text);
    setShowQr(false);
  };

  const getComponent = (type) => {
    if (vehicleComponents && vehicleComponents.length > 0) {
      const filteredComponent = vehicleComponents.filter(
        (component) => component.component_type === type,
      );

      const componentSerialNumber =
        filteredComponent.length > 0 ? filteredComponent[0].serial_number : null;

      return componentSerialNumber;
    }
    return null;
  };

  const getSim = () => {
    if (!isEmpty(activeSIMConfig)) {
      const simSerialNumber = activeSIMConfig?.links?.cellular?.length
        ? activeSIMConfig.links.cellular[0].sim : null;

      return simSerialNumber;
    }
    return null;
  };

  useEffect(() => {
    // validation
    setIsValid(false);
    switch (partType) {
      case "sc":
        if (sn2 && isMatching && provisioned && sim) setIsValid(true);
        if (!isNil(vehicleComponents) && !isEmpty(vehicleComponents)) setPartSN(getComponent("systemcontroller"));
        break;
      case "md":
        if (sn2 && isMatching) setIsValid(true);
        if (!isNil(vehicleComponents) && !isEmpty(vehicleComponents)) setPartSN(getComponent("motorcontroller"));
        break;
      case "bm":
        if (sn2 && isMatching) setIsValid(true);
        if (!isNil(vehicleComponents) && !isEmpty(vehicleComponents)) setPartSN(getComponent("battery"));
        break;
      case "sim":
        if (activeVehicle?.serial_number && sim) setIsValid(true);
        if (!isNil(activeSIMConfig) && !isEmpty(activeSIMConfig)) setPartSN(getSim());
        break;
      default:
        setIsValid(false);
    }
  }, [partType, sn1, sn2, provisioned, sim, isMatching, activeSIMConfig]);

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogTitle>{t("Vehicles.ReplaceComponent.ReplaceComponent")}</DialogTitle>
      <DialogContent style={{ maxWidth: "300px" }}>
        <Typography>{`${t("Vehicles.ReplaceComponent.VehicleID")}: ${
          activeVehicle?.shortcodes &&
          activeVehicle?.shortcodes.length &&
          activeVehicle?.shortcodes[0].shortcode
        }`}</Typography>
        <Typography>{`${t("Vehicles.ReplaceComponent.VehicleSN")}: ${
          activeVehicle?.serial_number
        }`}</Typography>
        <Typography>
          {partType && `${t(`Vehicles.ReplaceComponent.${partType}`)}:`} {partSN}
        </Typography>
        <FilledSelect
          label={t("Vehicles.ReplaceComponent.PartType")}
          value={partType}
          onChange={(e) => setPartType(e.target.value)}
          FormControlProps={{ fullWidth: true, margin: "normal" }}
        >
          {partTypes.map(
            (pt) =>
              pt && (
                <MenuItem value={pt} key={pt}>
                  {t(`Vehicles.PartTypes.${pt}`)}
                </MenuItem>
              ),
          )}
        </FilledSelect>
        <Collapse in={!!partType && partType !== "sim"} timeout={1000}>
          <FilledTextField
            value={sn1}
            underlineColor="white"
            label={t("Vehicles.ReplaceComponent.NewSerialNumber")}
            onChange={(e) => setSn1(e.target.value)}
            type={isMobile ? "qr-scan" : ""}
            onIconClick={() => setShowQr(true)}
          />
          <QrCodeScanner
            isOpen={showQr}
            onClose={() => setShowQr(false)}
            onScan={handleQrScan}
            partType={partType}
          ></QrCodeScanner>
          <FilledTextField
            value={sn2}
            underlineColor="white"
            label={t("Vehicles.ReplaceComponent.RetypeSerialNumber")}
            onChange={(e) => setSn2(e.target.value)}
            onBlur={() => setIsMatching(sn1 === sn2)}
            error={!isMatching}
            helperText={!isMatching && t("Vehicles.ReplaceComponent.RetypeWarning")}
          />
        </Collapse>
        <Collapse in={partType === "sc"} timeout={1000}>
          <FilledSelect
            label={t("Vehicles.ReplaceComponent.NewUsed")}
            value={provisioned}
            onChange={(e) => setProvisioned(e.target.value)}
            FormControlProps={{ fullWidth: true, margin: "normal" }}
          >
            <MenuItem value="new">{t("Vehicles.ReplaceComponent.New")}</MenuItem>
            <MenuItem value="used">{t("Vehicles.ReplaceComponent.Used")}</MenuItem>
          </FilledSelect>
          <FilledTextField
            value={sim}
            underlineColor="white"
            label={t("Vehicles.ReplaceComponent.SIM")}
            onChange={(e) => setSim(e.target.value)}
          />
        </Collapse>
        <Collapse in={partType === "sim"} timeout={1000}>
          <FilledTextField
            value={sim}
            underlineColor="white"
            label={t("Vehicles.ReplaceComponent.SIM")}
            onChange={(e) => setSim(e.target.value)}
          />
        </Collapse>
        <Collapse in={isLoading} timeout={1000}>
          <LinearProgress color="secondary" />
        </Collapse>
        <Collapse in={!!failure} timeout={1000}>
          <Typography color="error">{failure}</Typography>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t("Vehicles.ReplaceComponent.Cancel")}</Button>
        <Button onClick={handleSubmit} disabled={!isValid}>
          {t("Vehicles.ReplaceComponent.Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SwapComponent;
